import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Building2, Handshake, ShieldCheck } from "lucide-react";

export function Partners() {
  return (
    <div className="pb-16">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden min-h-[60vh] flex items-center">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/video/Partner.mp4" type="video/mp4" />
          </video>
          {/* Overlay to ensure text readability */}
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="text-white/90 font-bold tracking-wide uppercase text-sm bg-brand-primary/80 px-4 py-1 rounded-full inline-block backdrop-blur-sm">
              Collaborazioni
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white leading-tight">
              I Nostri Partner di Eccellenza
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
              Crediamo nel potere della collaborazione. I nostri partner di fiducia condividono i nostri valori 
              e la nostra visione, contribuendo a creare un network di eccellenza per la tua salute.
            </p>
          </div>
        </div>
      </section>

      {/* Main Partners Section */}
      <section className="py-16">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
            {/* Gruppo Sapio */}
            <a 
              href="https://www.grupposapio.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center text-center h-full hover:shadow-xl transition-all hover:-translate-y-1 block"
            >
              <div className="w-full h-32 mb-6 flex items-center justify-center bg-white rounded-lg p-4">
                <img 
                  src="/images/loghi/sapio.webp" 
                  alt="Logo Gruppo Sapio" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h2 className="text-xl font-heading font-bold text-text-title mb-4">Gruppo Sapio</h2>
              <p className="text-text-body text-sm mb-6 flex-grow">
                Azienda leader a livello italiano ed europeo nel settore dei gas tecnici e medicinali. 
                Garanzia di affidabilità e innovazione tecnologica da oltre 90 anni.
              </p>
              <div className="flex items-center text-xs text-gray-500 font-medium w-full justify-center pt-4 border-t border-gray-100">
                <ShieldCheck className="w-4 h-4 mr-2 text-brand-secondary" />
                Partner Industriale
              </div>
            </a>

            {/* Sapio Life */}
            <a 
              href="https://www.sapiolife.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center text-center h-full hover:shadow-xl transition-all hover:-translate-y-1 block"
            >
              <div className="w-full h-32 mb-6 flex items-center justify-center bg-white rounded-lg p-4">
                <img 
                  src="/images/loghi/sapiolife.webp" 
                  alt="Logo Sapio Life" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h2 className="text-xl font-heading font-bold text-text-title mb-4">Sapio Life</h2>
              <p className="text-text-body text-sm mb-6 flex-grow">
                Realtà dedicata ai servizi di Home Care del Gruppo Sapio, specializzata in terapie domiciliari
                e fornitura di ausili per la mobilità e la vita quotidiana.
              </p>
              <div className="flex items-center text-xs text-gray-500 font-medium w-full justify-center pt-4 border-t border-gray-100">
                <Handshake className="w-4 h-4 mr-2 text-brand-secondary" />
                Home Care Partner
              </div>
            </a>

            {/* Life Cure */}
            <a 
              href="https://lifecure.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center text-center h-full hover:shadow-xl transition-all hover:-translate-y-1 block"
            >
              <div className="w-full h-32 mb-6 flex items-center justify-center bg-white rounded-lg p-4">
                <img 
                  src="/images/loghi/Lifecure.webp" 
                  alt="Logo Life Cure" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h2 className="text-xl font-heading font-bold text-text-title mb-4">Life Cure</h2>
              <p className="text-text-body text-sm mb-6 flex-grow">
                Specializzata nella gestione delle cure domiciliari integrate (ADI). 
                Garantisce continuità assistenziale per pazienti ad alta complessità.
              </p>
              <div className="flex items-center text-xs text-gray-500 font-medium w-full justify-center pt-4 border-t border-gray-100">
                <Handshake className="w-4 h-4 mr-2 text-brand-secondary" />
                Partner Assistenziale
              </div>
            </a>

            {/* SonnoService */}
            <a 
              href="https://www.sonnoservice.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center text-center h-full hover:shadow-xl transition-all hover:-translate-y-1 block"
            >
              <div className="w-full h-32 mb-6 flex items-center justify-center bg-white rounded-lg p-4">
                <img 
                  src="/images/loghi/sonnoservice.webp" 
                  alt="Logo SonnoService" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h2 className="text-xl font-heading font-bold text-text-title mb-4">SonnoService</h2>
              <p className="text-text-body text-sm mb-6 flex-grow">
                Centro specializzato nella diagnosi e cura dei disturbi del sonno. 
                Soluzioni avanzate per apnee notturne e polisonnografia domiciliare.
              </p>
              <div className="flex items-center text-xs text-gray-500 font-medium w-full justify-center pt-4 border-t border-gray-100">
                <ShieldCheck className="w-4 h-4 mr-2 text-brand-secondary" />
                Partner Specialistico
              </div>
            </a>
          </div>

          {/* Institutional Partners */}
          <div className="text-center max-w-4xl mx-auto">
            <h3 className="text-2xl font-heading font-bold text-text-title mb-12">
              Collaborazioni Istituzionali
            </h3>
            <div className="grid sm:grid-cols-2 gap-8">
              <div className="flex items-center p-6 bg-gray-50 rounded-xl border border-gray-100">
                <div className="h-12 w-12 bg-brand-neutral rounded-full flex items-center justify-center text-brand-primary mr-4 shrink-0">
                  <Building2 className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-text-title">Servizio Sanitario Nazionale</h4>
                  <p className="text-sm text-text-body">Convenzionati con le principali ASL pugliesi</p>
                </div>
              </div>
              <div className="flex items-center p-6 bg-gray-50 rounded-xl border border-gray-100">
                <div className="h-12 w-12 bg-brand-neutral rounded-full flex items-center justify-center text-brand-primary mr-4 shrink-0">
                  <Building2 className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-text-title">Enti Ospedalieri</h4>
                  <p className="text-sm text-text-body">Collaborazione diretta per dimissioni protette</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-primary py-16 text-white">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-heading font-bold">Vuoi diventare nostro partner?</h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Siamo sempre alla ricerca di nuove sinergie per migliorare la qualità della vita dei nostri pazienti.
          </p>
          <div className="pt-4">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contatti">
                Contattaci per collaborare <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
